import React, { LegacyRef, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { HexColor } from '@r1-frontend/ui-kit/themes/colors/types';
import { ThemedProperties } from '@r1-frontend/ui-kit/themes/types';

import getAnimation from './helpers/getAnimation';
import getColors from './helpers/getColors';
import getResponsiveSizeStyles from './helpers/getResponsiveSizeStyles';

export type ColorType = HexColor | 'transparent';
export type TButtonType = 'accent'| 'primary' | 'secondary' | 'outline' | 'ghost';

export type TButtonProps = {
    /**
     * Тип кнопки
     *
     * @default accent
     */
    $type?: TButtonType,

    /**
     * Меньший размер
     *
     * @default false
     */
    $small?: boolean,

    /**
     * Анимация при нажатии
     *
     * @default true
     */
    $withAnimation?: boolean,

    icon?: ReactNode,
    ref?: LegacyRef<HTMLButtonElement>,
};

const Button = styled(
    ({ children, icon, ...props }: TButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>) => (
        <button {...props}>
            <span>
                {icon}
                {children}
            </span>
        </button>
    ),
)`
    position: relative;
    border: none;
    box-sizing: border-box;
    box-shadow: none;
    cursor: pointer;
    border-radius: 30px;

    ${getColors()};
    ${getResponsiveSizeStyles()};
    ${getAnimation()};

    ${({ $type, theme }: ThemedProperties<TButtonProps>) => css`
        font-weight: 500;
        border: ${$type === 'outline' ? `2px solid ${theme.colors.system.secondary}` : 'none'};

        &[disabled] {
            cursor: none;
            pointer-events: none;
            background-color: ${$type === 'outline' || $type === 'ghost' ? 'transparent' : theme.colors.system.disabled};
            color: ${theme.colors.text.disabled};
            border: ${$type === 'outline' ? `2px solid ${theme.colors.system.disabled}` : 'none'};
        }

        span {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: center;
            gap: ${theme.indents.xxs}px;
            white-space: nowrap;
            margin: ${theme.indents.xxxxxs};
        }
    `};
`;

Button.displayName = 'Button';

export { Button };
